<template>
  <div class="orchestrator-chat">
    <!-- Header -->
    <div class="chat-header">
      <div class="header-left">
        <div class="orchestrator-avatar">
          <i class="pi pi-microchip-ai" />
        </div>
        <div>
          <h2>Orquestador Central</h2>
          <span class="header-desc">Coordina múltiples agentes según tu petición</span>
        </div>
      </div>
      <div class="header-right">
        <Button
          icon="pi pi-list"
          text
          rounded
          severity="secondary"
          v-tooltip="'Ver workflows'"
          @click="showWorkflows = !showWorkflows"
        />
        <Button
          :icon="showUpload ? 'pi pi-times' : 'pi pi-paperclip'"
          :severity="showUpload ? 'secondary' : 'info'"
          text
          rounded
          v-tooltip="showUpload ? 'Cerrar adjuntos' : 'Adjuntar documentos'"
          @click="showUpload = !showUpload"
        />
        <Button
          icon="pi pi-history"
          text
          rounded
          severity="secondary"
          v-tooltip="'Historial'"
          @click="loadHistory"
        />
      </div>
    </div>

    <!-- Workflows panel -->
    <div v-if="showWorkflows" class="workflows-panel">
      <h3>Workflows disponibles</h3>
      <div v-if="workflowsLoading" class="workflows-loading">
        <ProgressBar mode="indeterminate" style="height: 4px" />
      </div>
      <div v-else class="workflows-grid">
        <div
          v-for="wf in workflows"
          :key="wf.id"
          class="workflow-card"
          @click="executeWorkflow(wf)"
        >
          <span class="workflow-name">{{ wf.name }}</span>
          <span class="workflow-desc">{{ wf.description }}</span>
          <Tag :value="`${wf.num_steps} pasos`" severity="info" size="small" />
        </div>
      </div>
    </div>

    <!-- Document upload panel -->
    <div v-if="showUpload" class="upload-panel">
      <DocumentUpload
        :requires-period="true"
        upload-label="Subir archivo financiero (Excel, CSV, PDF)"
        @uploaded="onDocumentUploaded"
        @error="onDocumentError"
      />
      <!-- Active documents -->
      <div v-if="sessionDocuments.length" class="active-docs">
        <div class="active-docs-title">Documentos cargados:</div>
        <div class="active-docs-list">
          <Tag
            v-for="doc in sessionDocuments"
            :key="doc.id"
            :value="doc.label"
            removable
            @remove="removeDocument(doc.id)"
            severity="success"
            class="doc-tag"
          />
        </div>
      </div>
    </div>

    <!-- Messages area -->
    <div class="messages" ref="messagesEl">
      <div v-if="messages.length === 0" class="empty-state">
        <i class="pi pi-microchip-ai empty-icon" />
        <p>Escribe tu petición. El orquestador decidirá automáticamente qué agente(s) ejecutar.</p>
        <div class="suggestions">
          <Button
            v-for="s in suggestions"
            :key="s"
            :label="s"
            text
            size="small"
            class="suggestion-btn"
            @click="inputText = s"
          />
        </div>
      </div>

      <div
        v-for="(msg, idx) in messages"
        :key="idx"
        :class="['message', msg.role]"
      >
        <div class="message-avatar">
          <i :class="['pi', msg.role === 'user' ? 'pi-user' : 'pi-microchip-ai']" />
        </div>
        <div class="message-content">
          <div class="message-text" v-html="formatText(msg.text)" />
          <div v-if="msg.meta" class="message-meta">
            <Tag v-if="msg.meta.strategy" :value="strategyLabel(msg.meta.strategy)" size="small" :severity="strategySeverity(msg.meta.strategy)" />
            <span v-if="msg.meta.tokens_used" class="meta-tokens">{{ msg.meta.tokens_used }} tokens</span>
            <span v-if="msg.meta.execution_time_ms" class="meta-time">{{ msg.meta.execution_time_ms }}ms</span>
          </div>
          <div v-if="msg.role === 'assistant' && msg.text" class="message-actions">
            <Button
              icon="pi pi-copy"
              size="small"
              text
              severity="secondary"
              label="Copiar"
              @click="copyToClipboard(msg.text)"
              v-tooltip.top="'Copiar respuesta al portapapeles'"
            />
          </div>
        </div>
      </div>

      <div v-if="loading" class="message assistant">
        <div class="message-avatar">
          <i class="pi pi-microchip-ai" />
        </div>
        <div class="message-content">
          <div v-if="statusMessage" class="status-message">{{ statusMessage }}</div>
          <div class="typing-indicator">
            <span></span><span></span><span></span>
          </div>
        </div>
      </div>
    </div>

    <!-- Input area -->
    <div class="input-area">
      <!-- Recording indicator -->
      <div v-if="isRecording" class="recording-indicator">
        <span class="recording-dot" />
        <span>Grabando... {{ formatDuration(recordingDuration) }}</span>
        <Button
          icon="pi pi-times"
          size="small"
          severity="danger"
          text
          rounded
          @click="cancelRecording"
          v-tooltip="'Cancelar'"
        />
      </div>
      <!-- Transcribing indicator -->
      <div v-if="isTranscribing" class="recording-indicator">
        <i class="pi pi-spin pi-spinner" />
        <span>Transcribiendo audio...</span>
      </div>
      <!-- Error toast -->
      <div v-if="recordError" class="record-error">
        <i class="pi pi-exclamation-triangle" />
        <span>{{ recordError }}</span>
        <Button icon="pi pi-times" size="small" text @click="recordError = null" />
      </div>
      <div class="input-row">
        <Textarea
          v-model="inputText"
          :auto-resize="true"
          :rows="1"
          :maxlength="2000"
          :placeholder="isRecording ? 'Escuchando...' : 'Escribe tu petición...'"
          class="chat-input"
          @keydown.enter.exact.prevent="sendMessage"
          :disabled="loading || isRecording"
        />
        <Button
          :icon="isRecording ? 'pi pi-stop' : 'pi pi-microphone'"
          :severity="isRecording ? 'danger' : 'secondary'"
          :disabled="loading || isTranscribing || !canRecord"
          :loading="isTranscribing"
          @click="isRecording ? handleStopRecording() : handleStartRecording()"
          rounded
          v-tooltip="isRecording ? 'Detener y transcribir' : 'Dictar por voz'"
          outlined
        />
        <Button
          icon="pi pi-send"
          :loading="loading"
          :disabled="!inputText.trim() || loading"
          @click="sendMessage"
          rounded
        />
      </div>
    </div>

    <!-- History dialog -->
    <Dialog v-model:visible="showHistory" header="Historial de orquestación" :modal="true" style="width: 600px">
      <div v-if="historyLoading">
        <ProgressBar mode="indeterminate" />
      </div>
      <div v-else-if="history.length === 0">
        <p>Sin historial aún.</p>
      </div>
      <div v-else class="history-list">
        <div v-for="h in history" :key="h.id" class="history-item">
          <div class="history-header">
            <Tag :value="h.strategy" size="small" />
            <span class="history-date">{{ formatDate(h.created_at) }}</span>
          </div>
          <p class="history-text">{{ h.request_text }}</p>
          <span class="history-tokens">{{ h.tokens_used }} tokens</span>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted } from 'vue';
import { useAuthStore } from '../../stores/auth';
import { orchestratorService } from '../../services/orchestrator';
import { useAudioRecorder } from '../../composables/useAudioRecorder';
import DocumentUpload from '../common/DocumentUpload.vue';
import Textarea from 'primevue/textarea';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';
import ProgressBar from 'primevue/progressbar';

const authStore = useAuthStore();

// Audio recorder
const {
  isRecording,
  isTranscribing,
  recordingDuration,
  error: recordError,
  canRecord,
  startRecording,
  stopAndTranscribe,
  cancelRecording,
} = useAudioRecorder();

const formatDuration = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const handleStartRecording = async () => {
  recordError.value = null;
  await startRecording();
};

const handleStopRecording = async () => {
  const tenantId = authStore.user?.tenant_id || `user_${authStore.user?.id || 0}`;
  const text = await stopAndTranscribe(tenantId, 'es-CO');
  if (text) {
    // Append to existing text or replace
    inputText.value = inputText.value
      ? `${inputText.value} ${text}`
      : text;
  }
};

// Document handlers
const onDocumentUploaded = (doc) => {
  if (!doc) return;
  const label = doc.filename || doc.description || `Documento #${doc.id}`;
  const period = doc.period_label || doc.periodLabel || '';
  sessionDocuments.value.push({
    id: doc.id,
    filename: doc.filename || doc.description || '',
    label: period ? `${label} (${period})` : label,
    period: period,
  });
};

const onDocumentError = (err) => {
  console.error('Error subiendo documento:', err);
};

const removeDocument = (docId) => {
  sessionDocuments.value = sessionDocuments.value.filter(d => d.id !== docId);
};

const inputText = ref('');
const messages = ref([]);
const loading = ref(false);
const statusMessage = ref('');
const messagesEl = ref(null);

const showWorkflows = ref(false);
const workflows = ref([]);
const workflowsLoading = ref(false);

const showUpload = ref(false);
const sessionDocuments = ref([]);

const showHistory = ref(false);
const history = ref([]);
const historyLoading = ref(false);

const suggestions = [
  'Análisis trimestral de KPIs',
  'Viabilidad de nueva sucursal',
  'Due diligence para inversor',
  'Auditoría fiscal completa',
];

const strategyLabel = (s) => {
  const map = { single_agent: 'Agente único', parallel_agents: 'Paralelo', workflow: 'Workflow' };
  return map[s] || s;
};

const strategySeverity = (s) => {
  const map = { single_agent: 'info', parallel_agents: 'warn', workflow: 'success' };
  return map[s] || 'secondary';
};

const formatText = (text) => {
  if (!text) return '';
  let html = text
    // Escape HTML
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Headers (## / ###)
    .replace(/^### (.+)$/gm, '<h4 style="margin:0.5em 0 0.25em;font-weight:600">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 style="margin:0.6em 0 0.3em;font-weight:700">$1</h3>')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Unordered lists
    .replace(/^- (.+)$/gm, '<li style="margin-left:1.2em">$1</li>')
    // Line breaks
    .replace(/\n/g, '<br>');
  return html;
};

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('es-CO');
};

const scrollToBottom = async () => {
  await nextTick();
  if (messagesEl.value) {
    messagesEl.value.scrollTop = messagesEl.value.scrollHeight;
  }
};

const copiedIdx = ref(null);
const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    copiedIdx.value = Date.now();
    setTimeout(() => { copiedIdx.value = null; }, 2000);
  } catch (err) {
    // Fallback para navegadores sin Clipboard API
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    copiedIdx.value = Date.now();
    setTimeout(() => { copiedIdx.value = null; }, 2000);
  }
};

const sendMessage = async () => {
  const text = inputText.value.trim();
  if (!text || loading.value) return;

  // Construir texto con contexto de documentos si hay adjuntos
  let enrichedText = text;
  if (sessionDocuments.value.length > 0) {
    const docSummary = sessionDocuments.value.map(d => d.label).join(', ');
    enrichedText = `${text}\n\n[Documentos adjuntos: ${docSummary}]`;
  }

  messages.value.push({ role: 'user', text: enrichedText });
  inputText.value = '';
  loading.value = true;
  statusMessage.value = sessionDocuments.value.length > 0
    ? 'Procesando documentos adjuntos...'
    : '';
  await scrollToBottom();

  try {
    // Pasar contexto de documentos al orquestador
    const context = sessionDocuments.value.length > 0 ? {
      documents: sessionDocuments.value.map(d => ({
        id: d.id,
        filename: d.filename,
        period: d.period,
      })),
    } : null;

    if (context) {
      statusMessage.value = 'Analizando datos financieros...';
    }

    const result = await orchestratorService.chat(text, authStore.user?.tenant_id, context);
    statusMessage.value = '';
    messages.value.push({
      role: 'assistant',
      text: result.summary || 'Respuesta recibida sin resumen.',
      meta: {
        strategy: result.strategy,
        tokens_used: result.tokens_used,
        provider_name: result.provider_name,
        model_name: result.model_name,
        workflow_id: result.workflow_id,
      },
    });
  } catch (err) {
    const detail = err.response?.data?.detail || 'Error de conexión con el orquestador.';
    messages.value.push({
      role: 'assistant',
      text: `Error: ${detail}`,
      meta: { strategy: 'error' },
    });
  } finally {
    loading.value = false;
    statusMessage.value = '';
    await scrollToBottom();
  }
};

const executeWorkflow = async (wf) => {
  showWorkflows.value = false;
  const text = `Ejecutar workflow: ${wf.name}`;
  messages.value.push({ role: 'user', text });
  loading.value = true;
  statusMessage.value = sessionDocuments.value.length > 0
    ? 'Procesando documentos adjuntos...'
    : '';
  await scrollToBottom();

  try {
    const context = sessionDocuments.value.length > 0 ? {
      documents: sessionDocuments.value.map(d => ({
        id: d.id,
        filename: d.filename,
        period: d.period,
      })),
    } : null;

    if (context) {
      statusMessage.value = 'Analizando datos financieros...';
    }

    const result = await orchestratorService.runWorkflow(
      wf.id, text, authStore.user?.tenant_id, context
    );
    statusMessage.value = '';
    messages.value.push({
      role: 'assistant',
      text: result.summary || `Workflow "${wf.name}" completado.`,
      meta: {
        strategy: 'workflow',
        tokens_used: result.tokens_used,
        workflow_id: result.workflow_id,
      },
    });
  } catch (err) {
    const detail = err.response?.data?.detail || 'Error ejecutando workflow.';
    messages.value.push({ role: 'assistant', text: `Error: ${detail}`, meta: { strategy: 'error' } });
  } finally {
    loading.value = false;
    statusMessage.value = '';
    await scrollToBottom();
  }
};

const loadWorkflows = async () => {
  workflowsLoading.value = true;
  try {
    workflows.value = await orchestratorService.listWorkflows();
  } catch {
    workflows.value = [];
  } finally {
    workflowsLoading.value = false;
  }
};

const loadHistory = async () => {
  showHistory.value = true;
  historyLoading.value = true;
  try {
    const tenantId = authStore.user?.tenant_id;
    if (tenantId) {
      const data = await orchestratorService.getHistory(tenantId);
      history.value = data.histories || [];
    }
  } catch {
    history.value = [];
  } finally {
    historyLoading.value = false;
  }
};

onMounted(() => {
  loadWorkflows();
});
</script>


<style scoped>
.orchestrator-chat {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 8rem);
  max-width: 900px;
  margin: 0 auto;
  background: var(--surface-card, #fff);
  border-radius: 12px;
  border: 1px solid var(--surface-border, #dee2e6);
  overflow: hidden;
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--surface-border, #dee2e6);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.orchestrator-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.2rem;
}

.header-left h2 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.header-desc {
  font-size: 0.8rem;
  color: var(--text-color-secondary, #6c757d);
}

.header-right {
  display: flex;
  gap: 0.25rem;
}

/* Workflows panel */
.workflows-panel {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--surface-border, #dee2e6);
  background: var(--surface-50, #f8f9fa);
}

/* Upload panel */
.upload-panel {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--surface-border, #dee2e6);
  background: var(--surface-50, #f8f9fa);
  max-height: 45vh;
  overflow-y: auto;
}
.active-docs {
  margin-top: 0.75rem;
}
.active-docs-title {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-color-secondary, #6c757d);
  margin-bottom: 0.4rem;
}
.active-docs-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}
.doc-tag {
  font-size: 0.78rem;
}

.workflows-panel h3 {
  margin: 0 0 0.75rem;
  font-size: 0.9rem;
  font-weight: 600;
}

.workflows-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
}

.workflow-card {
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--surface-border, #dee2e6);
  background: var(--surface-card, #fff);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  transition: border-color 0.15s;
}

.workflow-card:hover {
  border-color: var(--primary-color, #6366f1);
}

.workflow-name {
  font-weight: 600;
  font-size: 0.85rem;
}

.workflow-desc {
  font-size: 0.75rem;
  color: var(--text-color-secondary, #6c757d);
}

/* Messages */
.messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 1rem;
  color: var(--text-color-secondary, #6c757d);
}

.empty-icon {
  font-size: 3rem;
  color: var(--primary-color, #6366f1);
  opacity: 0.4;
}

.suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  max-width: 500px;
}

.suggestion-btn {
  border: 1px solid var(--surface-border, #dee2e6) !important;
  border-radius: 20px !important;
  font-size: 0.8rem !important;
}

.message {
  display: flex;
  gap: 0.75rem;
  max-width: 85%;
}

.message.user {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.message-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.85rem;
}

.message.user .message-avatar {
  background: var(--primary-color, #6366f1);
  color: #fff;
}

.message.assistant .message-avatar {
  background: var(--surface-200, #e9ecef);
  color: var(--text-color, #495057);
}

.message-content {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.message-text {
  padding: 0.75rem 1rem;
  border-radius: 12px;
  font-size: 0.9rem;
  line-height: 1.5;
}

.message.user .message-text {
  background: var(--primary-color, #6366f1);
  color: #fff;
  border-bottom-right-radius: 4px;
}

.message.assistant .message-text {
  background: var(--surface-100, #f1f3f5);
  color: var(--text-color, #495057);
  border-bottom-left-radius: 4px;
}

.message-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.7rem;
  color: var(--text-color-secondary, #6c757d);
}

.meta-tokens, .meta-time {
  opacity: 0.7;
}

.message-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.25rem;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.message:hover .message-actions {
  opacity: 1;
}

.message-actions .p-button {
  font-size: 0.72rem;
  padding: 0.2rem 0.5rem;
  gap: 0.3rem;
}

/* Status message */
.status-message {
  font-size: 0.82rem;
  color: var(--primary-color, #6366f1);
  font-weight: 500;
  padding: 0 1rem;
  margin-bottom: 0.25rem;
  animation: pulse-text 2s infinite;
}

@keyframes pulse-text {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Typing indicator */
.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 0.75rem 1rem;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--text-color-secondary, #6c757d);
  animation: typing 1.4s infinite;
}

.typing-indicator span:nth-child(2) { animation-delay: 0.2s; }
.typing-indicator span:nth-child(3) { animation-delay: 0.4s; }

@keyframes typing {
  0%, 60%, 100% { opacity: 0.3; transform: scale(0.8); }
  30% { opacity: 1; transform: scale(1); }
}

/* Recording indicators */
.recording-indicator {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.85rem;
  margin-bottom: 0.5rem;
  border-radius: 8px;
  background: var(--surface-50, #f8f9fa);
  border: 1px solid var(--surface-border, #dee2e6);
  font-size: 0.82rem;
  color: var(--text-color, #495057);
}
.recording-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ef4444;
  animation: rec-pulse 1s infinite;
}
@keyframes rec-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}
.record-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.75rem;
  margin-bottom: 0.5rem;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 0.8rem;
}

/* Input area */
.input-area {
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--surface-border, #dee2e6);
}

.input-row {
  display: flex;
  gap: 0.75rem;
  align-items: flex-end;
}

.chat-input {
  flex: 1;
  resize: none;
}

/* History dialog */
.history-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.history-item {
  padding: 0.75rem;
  border: 1px solid var(--surface-border, #dee2e6);
  border-radius: 8px;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.35rem;
}

.history-date {
  font-size: 0.75rem;
  color: var(--text-color-secondary, #6c757d);
}

.history-text {
  margin: 0;
  font-size: 0.85rem;
  color: var(--text-color, #495057);
}

.history-tokens {
  font-size: 0.7rem;
  color: var(--text-color-secondary, #6c757d);
}

.workflows-loading {
  padding: 1rem 0;
}
</style>
